import * as Sentry from "@sentry/react";
import { handleError } from "./errorHandler";

const setupGlobalErrorHandler = () => {
  const shouldIgnore = (
    errLike: unknown,
    source?: string | undefined,
  ): boolean => {
    try {
      const msg =
        typeof errLike === "string"
          ? errLike
          : (errLike as any)?.message || "";
      const stack = (errLike as any)?.stack || "";
      const src = source || "";

      // Known noisy sources to ignore (chat widgets, extensions, devtools, etc.)
      const patterns = [
        /\bTawk\b|\btwk-|\btawk\.to\b|\$_Tawk/i, // Tawk widget noise
        /Object Not Found Matching Id/i, // Chrome extension/devtools RPC noise
        /MethodName:\s*(simulateEvent|update)/i, // RPC method noise
        /ParamCount:\s*\d+/i,
      ];
      return patterns.some((p) => p.test(msg) || p.test(stack) || p.test(src));
    } catch {
      return false;
    }
  };

  window.onerror = function (message, source, lineno, colno, error) {
    if (shouldIgnore(error || message, source)) {
      // Returning true prevents the firing of the default event handler
      return true;
    }

    const errorMessage =
      typeof message === "string" ? message : "Unknown error";
    const errorObj = error instanceof Error ? error : new Error(errorMessage);
    handleError(errorObj, "Global onerror");
  };

  window.onunhandledrejection = function (event) {
    const raw = event?.reason;
    if (shouldIgnore(raw)) {
      return;
    }

    let reason: Error;
    if (raw instanceof Error) {
      reason = raw;
    } else if (typeof raw === "string") {
      reason = new Error(raw);
    } else {
      // Safely stringify unknown objects without throwing
      try {
        reason = new Error(JSON.stringify(raw));
      } catch {
        reason = new Error(String(raw));
      }
    }

    if (shouldIgnore(reason)) {
      return;
    }

    handleError(reason, "Global onunhandledrejection");
  };
};

export default setupGlobalErrorHandler;
